import React, { Fragment, memo, useCallback, useEffect, useRef } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { authService } from 'src/services/AuthService'

interface Props {
  children: React.ReactNode
}

/**
 * AuthKit exposes the access token only through React context, while every API call is made from
 * a saga running outside React. This registers the SDK's `getAccessToken` with `AuthService` so
 * sagas have one place to ask for a token, and unregisters it on sign-out.
 */
const WorkosAuthBridgeComponent = ({ children }: Props) => {
  const { getAccessToken, user } = useAuth()

  // Held in a ref so the registered provider is a stable function: registering
  // `getAccessToken` itself would swap the provider on every identity change, and a saga running
  // in that window would find none and fail as "Unauthorized".
  const getAccessTokenRef = useRef(getAccessToken)
  getAccessTokenRef.current = getAccessToken

  const registerProvider = useCallback(() => {
    // Without a signed-in WorkOS user `getAccessToken()` throws, so a provider is only
    // registered once there is a session behind it.
    if (!user) {
      authService.setAccessTokenProvider(null)

      return
    }

    authService.setAccessTokenProvider(() => {
      return getAccessTokenRef.current()
    })
  }, [user])

  // Deliberately during render, not in an effect. React runs effects child-first, so the route
  // guard below this component would dispatch its first request — and fail as "Unauthorized" —
  // before a parent's effect could register anything. Rendering happens parent-first, so doing
  // it here means the provider is in place before any descendant renders, let alone fetches.
  registerProvider()

  useEffect(() => {
    return () => {
      authService.setAccessTokenProvider(null)
    }
  }, [])

  return <Fragment>{children}</Fragment>
}

export const WorkosAuthBridge = memo(WorkosAuthBridgeComponent)
